import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Состояние пути записи: кто, к кому, на что и когда. Это единственное настоящее
 * локальное состояние в приложении — всё остальное живёт в кэше запросов.
 *
 * `appointmentId` и `holdExpiresAt` появляются, как только слот забронирован:
 * бронь живёт 15 минут, и таймер идёт уже во время анкеты.
 */

/** 'intake' — первичный приём из онбординга: анкета уже собрана, шаг формы не нужен. */
export type BookingFlow = 'new' | 'move' | 'intake';

export type Booking = {
  flow: BookingFlow;
  childId: number | null;
  specialistId: number | null;
  specialistName: string;
  serviceId: number | null;
  serviceTitle: string;
  durationMin: number | null;
  priceKop: number | null;
  isOnline: boolean;
  startsAt: number | null;
  /** Перенос: какую запись двигаем. */
  movingId: number | null;
  appointmentId: number | null;
  holdExpiresAt: number | null;
  intake: { name: string; age: string; note: string; phone: string; consent: boolean };
};

const EMPTY: Booking = {
  flow: 'new',
  childId: null,
  specialistId: null,
  specialistName: '',
  serviceId: null,
  serviceTitle: '',
  durationMin: null,
  priceKop: null,
  isOnline: false,
  startsAt: null,
  movingId: null,
  appointmentId: null,
  holdExpiresAt: null,
  intake: { name: '', age: '', note: '', phone: '', consent: false },
};

type Api = {
  booking: Booking;
  patch: (p: Partial<Booking>) => void;
  reset: (p?: Partial<Booking>) => void;
};

const Ctx = createContext<Api | null>(null);

export function BookingProvider({ children }: { children: ReactNode }) {
  const [booking, setBooking] = useState<Booking>(EMPTY);

  const patch = useCallback((p: Partial<Booking>) => setBooking((prev) => ({ ...prev, ...p })), []);
  const reset = useCallback((p?: Partial<Booking>) => setBooking({ ...EMPTY, ...p }), []);

  const value = useMemo(() => ({ booking, patch, reset }), [booking, patch, reset]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBooking(): Api {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('BookingProvider отсутствует выше по дереву');
  return ctx;
}

/**
 * Экран шага, открытый по прямой ссылке или после перезагрузки, не должен
 * показывать пустоту: возвращаемся на последний шаг, который пройден.
 *
 * Тот же расчёт нужен, когда истекает бронь: анкета и подтверждение остаются без
 * записи, и увести родителя надо к выбору времени, а не на главную.
 */
export function useBookingGuard(need: (b: Booking) => boolean): Booking | null {
  const { booking } = useBooking();
  const navigate = useNavigate();
  const ok = need(booking);
  if (!ok) {
    const to = lastCompletedStep(booking);
    queueMicrotask(() => navigate(to, { replace: true }));
    return null;
  }
  return booking;
}

export function lastCompletedStep(b: Booking): string {
  if (b.childId === null || b.specialistId === null) return '/';
  if (b.serviceId === null) return '/service';
  if (b.appointmentId === null) return '/calendar';
  return '/';
}
